import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  B2CFeatureFlagResponse,
  CheckRegistrationRequest,
  CheckRegistrationResponse,
  ConfirmRegistrationRequest,
  ConfirmRegistrationResponse,
  ENVIRONMENT,
} from '@ld.consumer-portal/models';
import { catchError, Observable, tap, throwError } from 'rxjs';

export type {
  B2CFeatureFlagResponse,
  CheckRegistrationRequest,
  CheckRegistrationResponse,
  ConfirmRegistrationRequest,
  ConfirmRegistrationResponse,
} from '@ld.consumer-portal/models';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || this.cpApiUrl;
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  checkToken(request: CheckRegistrationRequest): Observable<CheckRegistrationResponse> {
    if (request.loanNumber) {
      let params = new HttpParams().set('token', request.token).set('username', request.username || '');
      params = params.set('loanNumber', request.loanNumber);

      const url = `${this.cpApiUrl}/${this.apiNamespace}/register/check`;

      return this.http.get<CheckRegistrationResponse>(url, { params }).pipe(
        catchError((error) => {
          this.loggingService.logException(error, 'Error checking registration token');
          return throwError(() => new Error(`Failed to check token: ${error.message}`));
        })
      );
    } else {
      const params = new HttpParams().set('token', encodeURIComponent(request.token));
      const url = `${this.cpApiUrl}/${this.apiNamespace}/register/check-token`;

      return this.http.get<CheckRegistrationResponse>(url, { params }).pipe(
        catchError((error) => {
          this.loggingService.logException(error, 'Error checking token');
          return throwError(() => new Error(`Failed to check token: ${error.message}`));
        })
      );
    }
  }

  confirmRegistration(request: ConfirmRegistrationRequest): Observable<ConfirmRegistrationResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/register/confirm-login`;

    // eslint-disable-next-line no-console
    console.log('[RegistrationService] activateAccount URL:', url, 'request:', JSON.stringify({ ...request, ssn: '****', password: '***' }));

    return this.http.post<ConfirmRegistrationResponse>(url, request).pipe(
      // eslint-disable-next-line no-console
      tap(response => console.log('[RegistrationService] activateAccount response:', JSON.stringify({ ...response, activationToken: response.activationToken ? '***' : 'EMPTY', authToken: '***', createPasswordToken: '***' }))),
      catchError((error) => {
        this.loggingService.logException(error, 'Error confirming registration');
        return throwError(() => new Error(`Failed to confirm registration: ${error.message}`));
      })
    );
  }

  getB2CFeatureFlag(partnerId: string): Observable<B2CFeatureFlagResponse> {
    const params = new HttpParams().set('key', 'ADB2C_Enabled').set('partnerId', partnerId);

    const url = `${this.mlaApiUrl}/api/v1/configuration/keyvalue`;

    return this.http.get<B2CFeatureFlagResponse>(url, { params }).pipe(
      catchError((error) => {
        this.loggingService.logException(error, 'Error fetching B2C feature flag');
        return throwError(() => new Error(`Failed to fetch B2C feature flag: ${error.message}`));
      })
    );
  }
}
